import {produce} from 'immer';

import * as analytics from 'analytics/Analytics';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {assert, assertAndContinue} from 'Asserts';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FlowchartActionFunction} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  FlowchartAnalyticsMetadata,
  MODIFICATION_ACTION,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartEditor';
import {
  ProcessFlowchartInteractiveNode,
  ProcessInteractiveFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  DELETE_ROUTER_CONFIRMATION_MODAL_MESSAGE,
  DELETE_ROUTER_CONFIRMATION_MODAL_TITLE,
} from 'processes/modals/router-config/RouterConfigConstants';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';

import {updateRouterNodesUsingEdge} from './deleteEdgeProcessFlowchartActionFunction';

/**
 * When a node is deleted, ReactFlow renders the new graph without the edges
 * that were connected to the node, even if those edges are still present in our flowchart representation.
 * We need to also delete those edges in our flowchart representation.
 *
 * This does not modify the original flowchart, returns a new updated flowchart object.
 */
export function deleteNodeFromFlowchart(
  nodeClientId: string,
  flowchart: ProcessInteractiveFlowchart
): ProcessInteractiveFlowchart {
  let updatedNodes = flowchart.nodes.filter(({clientId}) => nodeClientId !== clientId);
  assertAndContinue(
    updatedNodes.length !== flowchart.nodes.length,
    `Node with ID ${nodeClientId} not found in flowchart`
  );
  const deletedEdges = flowchart.edges.filter(
    (edge) => edge.fromNodeClientId === nodeClientId || edge.toNodeClientId === nodeClientId
  );
  for (const deletedEdge of deletedEdges) {
    updatedNodes = updateRouterNodesUsingEdge(deletedEdge.clientId, updatedNodes);
  }
  const updatedEdges = flowchart.edges.filter(
    (edge) => edge.fromNodeClientId !== nodeClientId && edge.toNodeClientId !== nodeClientId
  );
  return produce(flowchart, (draftFlowchart) => {
    draftFlowchart.edges = updatedEdges;
    draftFlowchart.nodes = updatedNodes;
  });
}

/**
 * Before deleting a router node, we show a confirmation modal, as deleting a node deletes any configured functions.
 */
export async function deleteRouterNodeFromFlowchart(
  nodeClientId: string,
  flowchart: ProcessInteractiveFlowchart,
  editorEventGroupId: string,
  analyticsMetadata: FlowchartAnalyticsMetadata,
  actionOrigin: ActionOrigin
): Promise<ProcessInteractiveFlowchart> {
  const routerNode = flowchart.nodes.find(({clientId}) => clientId === nodeClientId);
  assert(
    routerNode != null && routerNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
    'Only router nodes in the flowchart can have router node action functions'
  );
  const routerConfig = routerNode.routerConfig;
  const isDefaultFunctionUnconfigured =
    routerConfig.defaultFunctionSpec == null || !routerConfig.defaultFunctionSpec.isConfigured;
  const areAllNonDefaultFunctionSpecsUnconfigured = routerNode.routerConfig.functionSpecs.every(
    (functionSpec) => !functionSpec.isConfigured
  );
  // If there are no configured functions, we don't run the confirmation modal
  if (isDefaultFunctionUnconfigured && areAllNonDefaultFunctionSpecsUnconfigured) {
    return deleteNodeFromFlowchart(nodeClientId, flowchart);
  }
  const confirmDelete = await runConfirmDeleteModal({
    typeName: 'router',
    title: DELETE_ROUTER_CONFIRMATION_MODAL_TITLE,
    message: DELETE_ROUTER_CONFIRMATION_MODAL_MESSAGE,
  });
  if (confirmDelete) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DELETED_ROUTER_CONFIGURATION_IN_CANVAS, {
      ...analyticsMetadata,
      event_group_id: editorEventGroupId,
      action_origin: actionOrigin,
      name: routerConfig.name,
      node_id: nodeClientId,
    });
    return deleteNodeFromFlowchart(nodeClientId, flowchart);
  }
  return flowchart;
}

export default function deleteNodeProcessFlowchartActionFunction(
  flowchart: ProcessInteractiveFlowchart,
  handleDelete: (
    nodeClientId: string,
    flowchart: ProcessInteractiveFlowchart,
    editorEventGroupId: string,
    analyticsMetadata: FlowchartAnalyticsMetadata,
    actionOrigin: ActionOrigin
  ) => Promise<ProcessInteractiveFlowchart> | ProcessInteractiveFlowchart,
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void,
  flowchartSchemaId: string,
  editorEventGroupId: string,
  analyticsMetadata: FlowchartAnalyticsMetadata,
  modalType?: string
): FlowchartActionFunction<ProcessFlowchartInteractiveNode, ProcessInteractiveFlowchart> {
  return {
    icon: 'delete',
    name: 'Delete node',
    tooltipText: 'Delete node',
    flowchart,
    updateHandler: onChangeFlowchart,
    onInvoke: async ({item, flowchart: ProcessInteractiveFlowchart, updateHandler}) => {
      const nodeId = item.clientId;
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MODIFIED_ITEM_IN_CANVAS, {
        ...standardAnalyticsMetadataToDict(analyticsMetadata),
        action_type: MODIFICATION_ACTION.DELETED_NODE,
        modified_item: flowchartSchemaId,
        nodes_modified: [nodeId],
        edges_modified: [],
        event_group_id: editorEventGroupId,
        action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
        modal_type: modalType,
      });
      const updatedFlowchart = await handleDelete(
        item.clientId,
        flowchart,
        editorEventGroupId,
        analyticsMetadata,
        ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS
      );
      updateHandler(updatedFlowchart);
    },
  };
}
